import React, { lazy, MutableRefObject, useEffect } from "react";
import {
  Errors,
  IOffer,
  Step,
  TechAvailabilityResponse,
} from "../../../../Interface/scheduler/form";
import ElementRenderer from "../../../../common/components/ElementRenderer/ElementRenderer";
import {
  AppConstants,
  SKELETON_LOADER_LAYOUT_PAGE,
  stepTypes,
} from "../../../../constants/constant";
import { useTechAvailabilityManager } from "../../hooks/useTechAvailabilityManager";
import BookAnInspectionPage from "../../../inspection/BookAnInspectionPage";
import { SUSPENSE_WRAPPER } from "../../../../common/components/SuspenseWrapper/SuspenseWrapper";
import { useFormConfigContext } from "../../../../common/contexts/FormConfigContext";
import { SelectedServices } from "../../../../Interface/quotation/form";
import { useShelfStorageConfig } from "../../hooks/useShelfStorageConfig";
import useIsMobile from "../../../../common/hooks/useIsMobile";
import OfferDetailsFooter from "../OffersPage/OfferDetailsFooter";
import { useFormContext } from "../../../../common/contexts/FormContext";
import { useSharedStateWithActions } from "../../../../common/hooks/useSharedState";
import { sharedStateKeys } from "../../../../constants/constant";
// import useUpdateSchedulerJson from "../../hooks/useUpdateSchedulerJson";
const CalendarPage = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/CalendarPage" */ "../CalendarPage/CalendarPage"
    ),
);
const NewCalendarPage = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/NewCalendarPage" */ "../../../newCalender/NewCalendarPage"
    ),
);
const PriceQuotation = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/PriceQuote" */ "../../../quotation/PriceQuoteContainer"
    ),
);
const VehiclePage = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/VehiclePage" */ "../../../vechicle/VehicleInformationPage/VehicleInformationPage"
    ),
);
const ServiceBotPage = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServiceBot" */ "../../../serviceBot/components/ServiceBotPage"
    ),
);
const PaymentPage = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/PaymentPage" */ "../../../paymentIframe/PaymentPage"
    ),
);
const CalendarPageStep = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/CalendarPageStep" */ "../../../calendar/CalendarPage"
    ),
);
const PricingCartStep = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/PricingCartStep" */ "../../../lawnServicePricing/PricingCartStep"
    ),
);

interface FormHandlerProps {
  currentStepConfig: Step;
  errors: Errors;
  setErrors: React.Dispatch<React.SetStateAction<Errors>>;
  scheduleCollection: TechAvailabilityResponse[];
  setSelectedScheduleId: (scheduleID: string) => void;
  setSelectedDate: (date: string) => void;
  setSelectedSlot: (slot: string) => void;
  setCalendarTimeSlot: (timeSlots: {
    startTime: string;
    endTime: string;
  }) => void;
  setInitialDate: (slot: string) => void;
  moveToNextPage: boolean;
  enableCallBack: boolean;
  setEnableCallBack: (disable: boolean) => void;
  setTechnicalModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleBack: () => void;
  handleNext: (id?, add?, isLAP?, skipBooking?) => void;
  handleSkip: (skipCalendar?: boolean) => void;
  nonPricablePressureWashingDataRef?: MutableRefObject<SelectedServices[]>;
  nonPricableQuoteObjRef?: MutableRefObject<any>;
  enable_new_calendar_design?: boolean;
  offerDetails?: IOffer;
  formActionButtons?: React.ReactNode;
}

const SchedulerStepHandler = ({
  currentStepConfig,
  errors,
  setErrors,
  scheduleCollection,
  setSelectedScheduleId,
  setSelectedDate,
  setSelectedSlot,
  setCalendarTimeSlot,
  setInitialDate,
  moveToNextPage,
  enableCallBack,
  setEnableCallBack,
  setTechnicalModal,
  handleBack,
  handleNext,
  handleSkip,
  nonPricablePressureWashingDataRef,
  nonPricableQuoteObjRef,
  enable_new_calendar_design,
  offerDetails,
  formActionButtons,
}: FormHandlerProps) => {
  const { formConfig } = useFormConfigContext();
  const { setFormValues, formValues } = useFormContext();
  const isMobile = useIsMobile();
  let useEffectTriggered = false;
  const displayOffer = currentStepConfig?.offersSection?.displaySpecialOffer;
  const { techAvailability, handleUpdateTechAvailability } =
    useTechAvailabilityManager(scheduleCollection);
  
  const { setValue } = useSharedStateWithActions();

  const { updatedStepConfig, hasShelfStorageField, configureShelfStorage } =
    useShelfStorageConfig({ currentStepConfig });

  // const { updateSchedulerJson } = useUpdateSchedulerJson();
  // Configure shelf storage when component mounts or when conditions change
  useEffect(() => {
    if (hasShelfStorageField) {
      configureShelfStorage();
      const elements = updatedStepConfig?.elements;
      const formElement = elements?.find((element) => element.type === "form");
      if (formElement?.fields?.length === 0 && !useEffectTriggered) {
        useEffectTriggered = true;
        setValue(sharedStateKeys.IS_SERVICE_PAGE_SKIPPED, true);

        if (formValues.isCalendarDisabled === true) {
          if (process.env.NODE_ENV !== 'prod') {
            console.log('Service page skipped and calendar disabled, calling handleSkip to save lead and redirect');
          }
          handleSkip(true);
        } else {
          handleNext("", "", false, true);
        }
      } else if (formElement?.fields?.length > 0) {
        setValue(sharedStateKeys.IS_SERVICE_PAGE_SKIPPED, false);
      }
    }
  }, [hasShelfStorageField, configureShelfStorage]);

  useEffect(() => {
    if (formConfig?.leadSourceId) {
      setFormValues((prevValues) => ({
        ...prevValues,
        configLeadSourceId: Number(formConfig.leadSourceId),
      }));
    }
  }, [formConfig?.leadSourceId, setFormValues]);

  // useEffect(() => {
  //   updateSchedulerJson();
  // }, [updateSchedulerJson]);

  const OffersFooterComponent = () => (
    <OfferDetailsFooter offerDetails={offerDetails} offerRemoved={false} />
  );

  const mobileFooter = () => {
    if (isMobile) {
      return (
        <>
          {displayOffer && <OffersFooterComponent />}
          {formActionButtons}
        </>
      );
    }
  };

  const renderCalendarPage = () => {
    if (currentStepConfig?.calendarNewFlow) {
      return SUSPENSE_WRAPPER(
        <CalendarPageStep
          element={currentStepConfig?.elements}
          techAvailability={techAvailability}
          onScheduleIdChange={setSelectedScheduleId}
          onDateChange={setSelectedDate}
          onSlotChange={setSelectedSlot}
          onTimeSlotChange={setCalendarTimeSlot}
          setInitialDate={setInitialDate}
          moveToNextPage={moveToNextPage}
          disableAll={enableCallBack}
          setDisableAll={setEnableCallBack}
          onUpdateTechAvailability={handleUpdateTechAvailability}
          hideUnavailableTimeSlots={currentStepConfig?.hideUnavailableTime}
        />,
        SKELETON_LOADER_LAYOUT_PAGE,
      );
    }

    const calendarComponent = enable_new_calendar_design ? (
      <NewCalendarPage
        element={currentStepConfig?.elements}
        techAvailability={scheduleCollection}
        onScheduleIdChange={setSelectedScheduleId}
        onDateChange={setSelectedDate}
        onSlotChange={setSelectedSlot}
        onTimeSlotChange={setCalendarTimeSlot}
        setInitialDate={setInitialDate}
        moveToNextPage={moveToNextPage}
        disableAll={enableCallBack}
        setDisableAll={setEnableCallBack}
        hideUnavailableTimeSlots={currentStepConfig?.hideUnavailableTime}
      />
    ) : (
      <CalendarPage
        element={currentStepConfig?.elements}
        techAvailability={scheduleCollection}
        onScheduleIdChange={setSelectedScheduleId}
        onDateChange={setSelectedDate}
        onSlotChange={setSelectedSlot}
        onTimeSlotChange={setCalendarTimeSlot}
        setInitialDate={setInitialDate}
        moveToNextPage={moveToNextPage}
        disableAll={enableCallBack}
        setDisableAll={setEnableCallBack}
        hideUnavailableTimeSlots={currentStepConfig?.hideUnavailableTime}
      />
    );
    return SUSPENSE_WRAPPER(calendarComponent, SKELETON_LOADER_LAYOUT_PAGE);
  };

  const renderCalendarStep = () => {
    return (
      <>
        {renderCalendarPage()}
        {mobileFooter()}
      </>
    );
  };

  const renderPriceQuotePage = () => {
    return (
      <>
        {SUSPENSE_WRAPPER(
          <PriceQuotation
            errors={errors}
            setErrors={setErrors}
            setTechnicalModal={setTechnicalModal}
            handleBack={handleBack}
            handleNext={handleNext}
            handleSkip={handleSkip}
            nonPricablePressureWashingDataRef={
              nonPricablePressureWashingDataRef
            }
            nonPricableQuoteObjRef={nonPricableQuoteObjRef}
          />,
          SKELETON_LOADER_LAYOUT_PAGE,
        )}
        {mobileFooter()}
      </>
    );
  };

  const renderVehicleInformation = () => {
    return (
      <>
        {SUSPENSE_WRAPPER(
          <VehiclePage
            vehicleConfig={currentStepConfig}
            errors={errors}
            setErrors={setErrors}
          />,
          SKELETON_LOADER_LAYOUT_PAGE,
        )}
        {mobileFooter()}
      </>
    );
  };

  const renderBookAnInspection = () => {
    return (
      <>
        <BookAnInspectionPage
          config={currentStepConfig}
          errors={errors}
          setErrors={setErrors}
        />
        {mobileFooter()}
      </>
    );
  };

  const renderServiceBotPage = () => {
    return (
      <>
        {SUSPENSE_WRAPPER(<ServiceBotPage />, SKELETON_LOADER_LAYOUT_PAGE)}
        {mobileFooter()}
      </>
    );
  };

  const renderPaymentPage = () => {
    return (
      <>
        {SUSPENSE_WRAPPER(
          <PaymentPage config={currentStepConfig} />,
          SKELETON_LOADER_LAYOUT_PAGE,
        )}
        {mobileFooter()}
      </>
    );
  };

  const renderPricingCartList = () => {
    return (
      <>
        {SUSPENSE_WRAPPER(
          <PricingCartStep
            config={currentStepConfig}
            handleBack={handleBack}
          />,
          SKELETON_LOADER_LAYOUT_PAGE,
        )}
        {mobileFooter()}
      </>
    );
  };

  const renderFormPage = (type: string) => {
    switch (type) {
      case AppConstants.REQUEST_TIME_TYPE:
        return renderCalendarStep();
      case stepTypes.STEP_PRICE_QUOTE_ESTIMATION:
        return renderPriceQuotePage();
      case AppConstants.VEHICLE_INFORMATION_SECTION:
        return renderVehicleInformation();
      case AppConstants.BOOK_AN_INSPECTION:
        return renderBookAnInspection();
      case AppConstants.SERVICE_BOT_SECTION:
        return renderServiceBotPage();
      case AppConstants.PAYMENT_IFRAME_SECTION:
        return renderPaymentPage();
      case AppConstants.PRICING_CART_LIST:
        return renderPricingCartList();
      default:
        return (
          <div
            className={`form-step-${currentStepConfig?.steptype} form-step-${formConfig?.conceptCode}`}
            id="formWithImageSection"
          >
            {updatedStepConfig?.elements?.map((element, index) => (
              <ElementRenderer
                key={element.id || index}
                element={element}
                errors={errors}
                setErrors={setErrors}
                formActionButtons={formActionButtons}
                offerDetails={offerDetails}
                displaySpecialOffer={displayOffer}
                applyOffer={currentStepConfig?.offerApply}
              />
            ))}
          </div>
        );
    }
  };
  return <>{renderFormPage(currentStepConfig?.steptype)}</>;
};

export default SchedulerStepHandler;
